import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../utils/api-client';
import { cancelTelegramLogin, pollTelegramLogin, startTelegramLogin } from '../auth';
import type { TelegramLoginStartResponse, WebSessionUser } from '../types';
import { getLocalizedApiErrorMessage } from '../utils/errorHelpers';
import { QrCode } from './QrCode';
import {
  canShowQrCode,
  formatCountdown,
  PRIMARY_BUTTON_CLASS,
  PRIMARY_BUTTON_STYLE,
  Spinner,
} from './telegramDeepLink';
import Icon from '../../miniapp/ui-kit/Icon';

type Phase = 'idle' | 'requesting' | 'waiting' | 'denied' | 'expired' | 'error' | 'unavailable';
type ServerStatus = 'pending' | 'opened';

interface TelegramLoginPanelProps {
  mode: 'login' | 'register';
  onAuthenticated: (user: WebSessionUser) => void;
  /** Called when the bot is not configured; the panel then renders nothing. */
  onUnavailable?: () => void;
}

export default function TelegramLoginPanel({
  mode,
  onAuthenticated,
  onUnavailable,
}: TelegramLoginPanelProps) {
  const { t, i18n } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [request, setRequest] = useState<TelegramLoginStartResponse | null>(null);
  const [serverStatus, setServerStatus] = useState<ServerStatus>('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [canShowQr] = useState(canShowQrCode);
  // The live request this panel owns; cleared on every terminal state so it is never cancelled twice
  // (and a completed sign-in is never cancelled on unmount).
  const activeRequestId = useRef<string | null>(null);
  const mounted = useRef(false);

  function resolveError(error: unknown): string {
    return getLocalizedApiErrorMessage(error, t, {
      exists: i18n.exists.bind(i18n),
      fallbackKey: 'website.telegramLogin.error',
    });
  }

  async function start(): Promise<void> {
    setPhase('requesting');
    setErrorMessage('');

    try {
      const response = await startTelegramLogin();
      if (!mounted.current) {
        void cancelTelegramLogin(response.requestId).catch(() => undefined);
        return;
      }

      activeRequestId.current = response.requestId;
      setRequest(response);
      setServerStatus('pending');
      setNow(Date.now());
      setPhase('waiting');
    } catch (error) {
      if (error instanceof ApiError && error.code === 'TELEGRAM_BOT_NOT_CONFIGURED') {
        setPhase('unavailable');
        onUnavailable?.();
        return;
      }

      setErrorMessage(resolveError(error));
      setPhase('error');
    }
  }

  function cancel(): void {
    const requestId = activeRequestId.current;
    activeRequestId.current = null;
    setRequest(null);
    setPhase('idle');
    if (requestId) {
      void cancelTelegramLogin(requestId).catch(() => undefined);
    }
  }

  /** Handles one poll result; returns whether polling should continue. */
  const handlePoll = useEffectEvent(async (requestId: string): Promise<boolean> => {
    try {
      const result = await pollTelegramLogin(requestId);
      if (activeRequestId.current !== requestId) {
        return false;
      }

      switch (result.status) {
        case 'pending':
        case 'opened':
          setServerStatus(result.status);
          return true;
        case 'authenticated':
          activeRequestId.current = null;
          onAuthenticated(result.user);
          return false;
        case 'denied':
          activeRequestId.current = null;
          setPhase('denied');
          return false;
        default:
          activeRequestId.current = null;
          setPhase('expired');
          return false;
      }
    } catch (error) {
      if (activeRequestId.current !== requestId) {
        return false;
      }

      activeRequestId.current = null;
      setErrorMessage(resolveError(error));
      setPhase('error');
      return false;
    }
  });

  const expireLocally = useEffectEvent(() => {
    activeRequestId.current = null;
    setPhase('expired');
  });

  // Polling: chained setTimeout (never overlapping), paused while the tab is hidden, and an
  // immediate poll when the tab becomes visible again (mobile: returning from Telegram).
  useEffect(() => {
    if (phase !== 'waiting' || !request) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let polling = false;
    let disposed = false;
    const expiresAt = Date.parse(request.expiresAt);

    const poll = async () => {
      if (disposed || polling || document.visibilityState === 'hidden') {
        return;
      }
      if (Date.now() >= expiresAt) {
        expireLocally();
        return;
      }

      polling = true;
      const keepPolling = await handlePoll(request.requestId);
      polling = false;
      if (keepPolling && !disposed) {
        timeoutId = setTimeout(() => void poll(), request.pollIntervalMs);
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        void poll();
      }
    };

    timeoutId = setTimeout(() => void poll(), request.pollIntervalMs);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      disposed = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [phase, request]);

  // Countdown tick while waiting.
  useEffect(() => {
    if (phase !== 'waiting') {
      return;
    }

    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [phase]);

  // Cancel a still-live request when the user leaves the page.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      const requestId = activeRequestId.current;
      activeRequestId.current = null;
      if (requestId) {
        void cancelTelegramLogin(requestId).catch(() => undefined);
      }
    };
  }, []);

  if (phase === 'unavailable') {
    return null;
  }

  const startLabel = mode === 'register' ? t('website.telegramLogin.register') : t('website.telegramLogin.login');

  if (phase === 'waiting' && request) {
    const remainingMs = Date.parse(request.expiresAt) - now;

    return (
      <section className="grid gap-4 rounded-2xl bg-secondary p-5">
        <div className="flex flex-col gap-5 md:flex-row md:items-center">
          {canShowQr ? (
            <div className="hidden shrink-0 flex-col items-center gap-2 md:flex">
              <QrCode value={request.deepLinkUrl} label={t('website.telegramLogin.scanLabel')} />
              <span className="text-xs text-text-secondary">{t('website.telegramLogin.scan')}</span>
            </div>
          ) : null}

          <div className="grid flex-1 gap-3">
            <a
              href={request.deepLinkUrl}
              target="_blank"
              rel="noreferrer"
              className={PRIMARY_BUTTON_CLASS}
              style={PRIMARY_BUTTON_STYLE}
            >
              <Icon name="send" className="text-[20px]" />
              {t('website.telegramLogin.open')}
            </a>
            <p role="status" aria-live="polite" className="flex items-center gap-2 text-sm text-text">
              <Spinner />
              {serverStatus === 'opened'
                ? t('website.telegramLogin.opened')
                : t('website.telegramLogin.pending')}
            </p>
            <p className="text-xs text-text-secondary">
              {t('website.telegramLogin.linkExpires', { countdown: formatCountdown(remainingMs) })}
            </p>
            <button
              type="button"
              className="justify-self-start text-xs font-medium text-text-secondary hover:underline"
              onClick={cancel}
            >
              {t('website.telegramLogin.cancel')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  const terminalMessage =
    phase === 'denied'
      ? t('website.telegramLogin.denied')
      : phase === 'expired'
        ? t('website.telegramLogin.expired')
        : phase === 'error'
          ? errorMessage
          : '';

  return (
    <section className="grid gap-3">
      {terminalMessage ? (
        <p
          role={phase === 'error' ? 'alert' : 'status'}
          className={
            phase === 'error'
              ? 'rounded-2xl bg-error-container px-4 py-3 text-sm text-on-error-container'
              : 'rounded-2xl bg-secondary px-4 py-3 text-sm text-text'
          }
        >
          {terminalMessage}
        </p>
      ) : null}

      <button
        type="button"
        className={PRIMARY_BUTTON_CLASS}
        style={PRIMARY_BUTTON_STYLE}
        disabled={phase === 'requesting'}
        onClick={() => void start()}
      >
        {phase === 'requesting' ? (
          <Spinner />
        ) : (
          <Icon name="send" className="text-[20px]" />
        )}
        {terminalMessage ? t('website.telegramLogin.retry') : startLabel}
      </button>
      {!terminalMessage ? (
        <p className="text-center text-xs text-text-secondary">{t('website.telegramLogin.helper')}</p>
      ) : null}
    </section>
  );
}
